import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedCard } from '@/components/ui/animated-card';
import { GlowButton } from '@/components/ui/glow-button';
import { 
  Store, 
  CheckCircle, 
  Users, 
  FileText, 
  DollarSign,
  Clock,
  AlertCircle,
  UserPlus,
  UserMinus,
  TrendingUp,
  Shield,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ActivityItem } from '@/services/dashboard.service';

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
  className?: string;
}

const activityIcons = {
  merchant_registered: Store,
  merchant_approved: CheckCircle,
  plan_created: FileText,
  payout_generated: DollarSign,
  user_subscribed: Users,
  merchant_application_submitted: Store,
  subscription_created: UserPlus,
  subscription_cancelled: UserMinus,
  redemption_surge: TrendingUp,
  admin_action: Shield,
} as const;

const activityColors = {
  merchant_registered: {
    icon: 'text-blue-600',
    bg: 'bg-gradient-to-br from-blue-500/20 to-indigo-500/10',
    border: 'border-blue-200/50',
    shadow: 'shadow-blue-500/10'
  },
  merchant_approved: {
    icon: 'text-green-600',
    bg: 'bg-gradient-to-br from-green-500/20 to-emerald-500/10',
    border: 'border-green-200/50',
    shadow: 'shadow-green-500/10'
  },
  plan_created: {
    icon: 'text-purple-600',
    bg: 'bg-gradient-to-br from-purple-500/20 to-violet-500/10',
    border: 'border-purple-200/50',
    shadow: 'shadow-purple-500/10'
  },
  payout_generated: {
    icon: 'text-orange-600',
    bg: 'bg-gradient-to-br from-orange-500/20 to-amber-500/10',
    border: 'border-orange-200/50',
    shadow: 'shadow-orange-500/10'
  },
  user_subscribed: {
    icon: 'text-indigo-600',
    bg: 'bg-gradient-to-br from-indigo-500/20 to-purple-500/10',
    border: 'border-indigo-200/50',
    shadow: 'shadow-indigo-500/10'
  },
  merchant_application_submitted: {
    icon: 'text-sky-600',
    bg: 'bg-gradient-to-br from-sky-500/20 to-blue-500/10',
    border: 'border-sky-200/50',
    shadow: 'shadow-sky-500/10'
  },
  subscription_created: {
    icon: 'text-emerald-600',
    bg: 'bg-gradient-to-br from-emerald-500/20 to-green-500/10',
    border: 'border-emerald-200/50',
    shadow: 'shadow-emerald-500/10'
  },
  subscription_cancelled: {
    icon: 'text-red-600',
    bg: 'bg-gradient-to-br from-red-500/20 to-rose-500/10',
    border: 'border-red-200/50',
    shadow: 'shadow-red-500/10'
  },
  redemption_surge: {
    icon: 'text-yellow-600',
    bg: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/10',
    border: 'border-yellow-200/50',
    shadow: 'shadow-yellow-500/10'
  },
  admin_action: {
    icon: 'text-slate-600',
    bg: 'bg-gradient-to-br from-slate-500/20 to-gray-500/10',
    border: 'border-slate-200/50',
    shadow: 'shadow-slate-500/10'
  },
} as const;

export function ActivityFeed({ activities, isLoading = false, className }: ActivityFeedProps) {
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  if (isLoading) {
    return (
      <AnimatedCard 
        gradient={true} 
        glow={true} 
        className={cn("shadow-brand-sm hover:shadow-brand-md transition-all duration-500 p-0", className)}
      >
        <CardHeader className="relative">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary animate-pulse" />
            <CardTitle className="gradient-text-primary">Recent Activity</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground/80">
            Latest platform updates and events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-4 p-3 rounded-xl bg-gradient-to-r from-slate-50/50 to-transparent border border-slate-100/50">
                <div className="w-10 h-10 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-xl shimmer flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-lg shimmer" />
                  <div className="h-3 w-1/2 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded shimmer" />
                </div>
                <div className="h-3 w-12 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded shimmer" />
              </div>
            ))}
          </div>
        </CardContent>
      </AnimatedCard>
    );
  }

  return (
    <AnimatedCard 
      gradient={true} 
      glow={true}
      className={cn(
        "shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative overflow-hidden",
        className
      )}
    >
      {/* Enhanced background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <CardHeader className="relative z-10">
        <CardTitle className="flex items-center gap-2 group-hover:gradient-text-primary transition-all duration-300">
          <Activity className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
          Recent Activity
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-pulse shadow-glow-sm" />
            <div className="w-1 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full animate-pulse opacity-60" />
          </div>
        </CardTitle>
        <CardDescription className="text-muted-foreground/80 group-hover:text-muted-foreground transition-colors">
          Latest platform updates and events
        </CardDescription>
      </CardHeader>
      
      <CardContent className="relative z-10">
        {activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200/50 mb-4">
              <Clock className="h-12 w-12 text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium mb-1">No recent activity</p>
            <p className="text-sm text-slate-500">
              Activity will appear here as events occur
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = activityIcons[activity.type] || AlertCircle;
              const colorConfig = activityColors[activity.type] || activityColors.admin_action;
              
              return (
                <div 
                  key={activity.id} 
                  className="group/item flex items-start gap-4 p-3 rounded-xl border border-transparent hover:border-slate-200/50 hover:bg-white/50 hover:backdrop-blur-sm transition-all duration-300 hover:shadow-brand-sm"
                >
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-sm",
                    colorConfig.bg,
                    colorConfig.border
                  )}>
                    <Icon className={cn("h-5 w-5", colorConfig.icon)} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 leading-tight group-hover/item:gradient-text-primary transition-all duration-300">
                      {activity.title}
                    </p>
                    <p className="text-sm text-slate-600 mt-1 group-hover/item:text-slate-700 transition-colors">
                      {activity.description}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0 text-xs text-slate-500 font-medium px-2 py-1 rounded-full bg-slate-100/50 group-hover/item:bg-slate-200/50 transition-colors">
                    {formatTimeAgo(activity.timestamp)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {activities.length > 0 && (
          <div className="mt-6 pt-4 border-t gradient-border-sm">
            <GlowButton 
              variant="outline-soft" 
              className="w-full text-sm font-medium group/view"
            >
              View all activity
              <Activity className="w-4 h-4 ml-2 group-hover/view:animate-pulse" />
            </GlowButton>
          </div>
        )}
      </CardContent>
    </AnimatedCard>
  );
}
